import React from 'react';
import { Box, TextField, IconButton, Typography, MenuItem, Button } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import NumericTextField from './inputs/NumericTextField';

interface StockEntry {
  stock_type: 'in_store' | 'online';
  stock: number;
}

interface StockLocation {
  name: string;
  stocks: StockEntry[];
}

interface StockInputProps {
  locations: StockLocation[];
  allLocations: { name: string }[];
  onLocationsChange: (updatedLocations: StockLocation[]) => void;
}

const StockInput: React.FC<StockInputProps> = ({ locations, allLocations, onLocationsChange }) => {
  const handleAddLocation = () => {
    onLocationsChange([
      ...locations,
      {
        name: '',
        stocks: [
          { stock_type: 'in_store', stock: 0 },
          { stock_type: 'online', stock: 0 },
        ],
      },
    ]);
  };

  const handleLocationNameChange = (index: number, name: string) => {
    const updated = [...locations];
    updated[index] = { ...locations[index], name };
    onLocationsChange(updated);
  };

  const handleStockChange = (index: number, stockType: 'in_store' | 'online', value: string) => {
    // Allow the field to be cleared without freezing edits (empty => 0).
    const numeric = value === '' ? 0 : parseInt(value, 10);
    if (isNaN(numeric)) return;

    const updated = [...locations];
    const stockIndex = updated[index].stocks.findIndex(s => s.stock_type === stockType);
    if (stockIndex !== -1) {
      const newStocks = [...updated[index].stocks];
      newStocks[stockIndex] = { ...newStocks[stockIndex], stock: numeric };
      updated[index] = { ...updated[index], stocks: newStocks };
      onLocationsChange(updated);
    }
  };

  const handleDeleteLocation = (index: number) => {
    onLocationsChange(locations.filter((_, i) => i !== index));
  };

  // Get available locations (not already selected)
  const getAvailableLocations = (currentIndex: number) => {
    const selectedNames = locations.map((loc, idx) => idx === currentIndex ? null : loc.name).filter(Boolean);
    return allLocations.filter(loc => !selectedNames.includes(loc.name) || loc.name === locations[currentIndex]?.name);
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        Stock by Location
      </Typography>
      
      {locations.map((location, index) => (
        <Box
          key={index}
          sx={{
            mb: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            p: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              select
              label="Location"
              value={location.name || ''}
              onChange={(e) => handleLocationNameChange(index, e.target.value)}
              size="small"
              sx={{ flex: 1 }}
            >
              <MenuItem value="" disabled>
                Select a location
              </MenuItem>
              {getAvailableLocations(index).map((loc) => (
                <MenuItem key={loc.name} value={loc.name}>
                  {loc.name}
                </MenuItem>
              ))}
            </TextField>
            
            <IconButton 
              onClick={() => handleDeleteLocation(index)}
              size="small"
              sx={{ color: 'error.light' }}
            >
              <Delete />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {(['in_store', 'online'] as const).map((type) => {
              const stockEntry = location.stocks.find(s => s.stock_type === type);
              const stockValue = stockEntry?.stock ?? 0;
              
              return (
                <NumericTextField
                  key={type}
                  label={type === 'in_store' ? 'In-Store Stock' : 'Online Stock'}
                  value={stockValue}
                  onValueChange={(n) => handleStockChange(index, type, String(n))}
                  allowEmpty
                  fallbackValue={0}
                  min={0}
                  size="small"
                  fullWidth
                  disabled={!location.name}
                />
              );
            })}
          </Box>
        </Box>
      ))}

      <Button
        startIcon={<Add />}
        onClick={handleAddLocation}
        size="small"
        variant="outlined"
        sx={{ mt: 1 }}
      >
        Add Location
      </Button>
    </Box>
  );
};

export default StockInput;